<script setup lang="ts">
import { RouterView, useRoute, useRouter } from "vue-router";
import LINESeedJP_OTF_Bd from "@/assets/fonts/LINESeedJP_OTF_Bd.woff2";
import LINESeedJP_OTF_Eb from "@/assets/fonts/LINESeedJP_OTF_Eb.woff2";
import FontFaceObserver from "fontfaceobserver";
import { onMounted, onUpdated, provide, ref } from "vue";
import NavigationMenu from "./navigation-menu.vue";
import FooterMenu from "./footer-menu.vue";
import { computed } from "vue";
import router from "@/router";
import AdminNotify from "@/component/AdminNotify.vue";
import AdminLoading from "@/component/AdminLoading.vue";
import {useUiTheme} from "./use-ui-theme.vue";

// フォントのオブザーバーを作成
const font = new FontFaceObserver("LINESeedJPStd");

// フォントがロードされたら実行される
font.load().then(
  () => {
    document.documentElement.classList.add("fonts-loaded");
  },
  () => {
    console.error("LINESeedJPStd font could not be loaded after 3 seconds.");
  }
);

const style = document.createElement("style");
style.textContent = `
  @font-face {
    font-family: 'LINESeedJPStd';
    src: url('${LINESeedJP_OTF_Bd}') format('woff2');
    font-weight: normal;
    font-style: normal;
    font-display: swap; /* ロード中はフォールバックフォントを使用 */
  }
  @font-face {
    font-family: 'LINESeedJPStd';
    src: url('${LINESeedJP_OTF_Eb}') format('woff2');
    font-weight: bold;
    font-style: normal;
    font-display: swap; /* ロード中はフォールバックフォントを使用 */
  }
  @font-face {
    font-family: "LINE Seed JP_TTF";
    src: url('${LINESeedJP_OTF_Bd}') format('woff2');
    font-weight: bold;
    font-style: normal;
    font-display: swap; /* ロード中はフォールバックフォントを使用 */
  }
  @font-face {
    font-family: "LINE Seed JP_TTF";
    src: url('${LINESeedJP_OTF_Eb}') format('woff2');
    font-weight: 800;
    font-style: normal;
    font-display: swap; /* ロード中はフォールバックフォントを使用 */
  }
`;
document.head.append(style);

const uiTheme = useUiTheme()
provide('uiTheme', uiTheme);

const meta = computed(() => {
  return router.currentRoute.value.meta;
});

const showNavigationMenu = computed(() => {
  return router.currentRoute.value.meta.showNavigationMenu !== false;
});
</script>

<script lang="ts">
const prev = ref<string | undefined>();
const hamburger = ref<"left" | "right" | "none" | null>(null);
const hamburgerClick = ref<() => void>(() => {});

export const setPrevLink = (value?: string) => {
  prev.value = value;
};

export const setHamburger = (value: "left" | "right" | "none", callback: () => void) => {
  hamburger.value = value;
  hamburgerClick.value = callback;
};

const isVisible = ref(true);
const sidebar_animation = ref<"" | "navigation-show" | "navigation-hide">("");
const content_animation = ref<"" | "content-expand" | "content-shrink">("");
const overlay_animation = ref<"" | "overlay-show" | "overlay-hide">("");

const setTransitionDuration = (duration: string) => {
  document.documentElement.style.setProperty("--transition-duration", duration);
};

const resetTransitionDuration = () => {
  setTimeout(() => {
    setTransitionDuration("0s");
  }, 300);
};

export const setVisible = (value: boolean) => {
  isVisible.value = value;

  if (!isVisible.value) {
    content_animation.value = "content-expand";
  }
};

export const setOpen = (value: boolean) => {
  setTransitionDuration("0.3s");

  sidebar_animation.value = value ? "navigation-show" : "navigation-hide";
  content_animation.value = value ? "content-shrink" : "content-expand";
  overlay_animation.value = value ? "overlay-show" : "overlay-hide";

  if (value) {
    setHamburger("none", () => {});
  } else {
    setHamburger("right", () => {});
  }

  resetTransitionDuration();
};

let prevWidth = 0;
const updateHamburgerState = () => {
  if (prevWidth >= 1024 && document.body.clientWidth < 1024) {
    setOpen(false);
  } else if (prevWidth < 1024 && document.body.clientWidth >= 1024) {
    setOpen(true);
  }
  prevWidth = document.body.clientWidth;
};

setOpen(false);
updateHamburgerState();

window.addEventListener("resize", () => {
  updateHamburgerState();
});

const loading = ref(false);

let loadingCounter = 0;

export const setLoading = (value: boolean) => {
  if (value) {
    loadingCounter++;
  } else {
    if (loadingCounter > 0) {
      loadingCounter--;
    }
  }
  if (loadingCounter > 0) {
    const appElm = document.querySelector("#app") as HTMLDivElement;
    if (appElm) {
      appElm.style.overflow = "hidden";
    }
    loading.value = true;
  }
  if (loadingCounter === 0) {
    const appElm = document.querySelector("#app") as HTMLDivElement;
    if (appElm) {
      appElm.style.overflow = "unset";
    }
    loading.value = false;
  }
};

const notify = ref();
export const showNotify = (success: boolean, title: string, body: string) => {
  notify.value.show(success, title, body);
};
</script>

<template>
  <AdminLoading :loading="loading" full-screen />
  <AdminNotify ref="notify" />
  <template v-if="!showNavigationMenu">
    <RouterView class="bg-base text-main-text flex flex-row" />
  </template>
  <template v-else-if="meta.forPrint">
    <RouterView />
  </template>
  <div class="bg-base text-main-text relative flex flex-row" v-else>
    <RouterView ref="content" v-slot="{ Component }">
      <Component :is="Component" :content_animation="content_animation" />
    </RouterView>
    <div class="overlay fixed z-10 h-full w-screen lg:hidden" :class="overlay_animation" @click="setOpen(false)"></div>
    <div v-if="isVisible">
      <div class="border-outline fixed right-0 z-20 h-full min-h-screen w-60 min-w-60 border-l" :class="sidebar_animation">
        <NavigationMenu />
      </div>
      <div
        class="bg-base border-base-button fixed right-1 top-1 z-20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border transition-all duration-150 hover:bg-base-button-hover"
        @click="setOpen(hamburger === 'none' ? false : true)"
      >
        <i :class="'fa-solid fa-bars text-main-text'" />
      </div>
    </div>
    <FooterMenu class="lg:hidden" />
  </div>
</template>
<style lang="scss">
:root {
  --transition-duration: 0s;
}

.navigation {
  width: 100%;
  transform: translateX(15rem);

  @media (min-width: 1024px) {
    transform: translateX(0px);
  }
}

.overlay {
  display: none;
  background-color: transparent;
}

.navigation-show {
  will-change: transform;
  transform: translateX(0rem);
  transition-duration: var(--transition-duration);
  transition-timing-function: ease-out;
}

.navigation-hide {
  width: 0%;
  will-change: transform;
  transform: translateX(15rem);
  transition-duration: var(--transition-duration);
  transition-timing-function: ease-out;
}

.overlay-show {
  @media (max-width: 1023px) {
    animation-name: DarkOutAnime;
    animation-duration: var(--transition-duration);
    animation-fill-mode: forwards;
    animation-timing-function: ease-out;
    display: block;
    background-color: transparent;
  }
}

.overlay-hide {
  @media (max-width: 1023px) {
    animation-name: DarkInAnime;
    animation-duration: var(--transition-duration);
    animation-fill-mode: forwards;
    animation-timing-function: ease-out;
    display: none;
    background-color: transparent;
  }
}

.safearea-bottom {
  padding-bottom: env(safe-area-inset-bottom);
}

.content {
  width: 100%;
}

.h-screen-without-header {
  height: calc(100dvh - 2.5rem);
}

.max-h-screen-without-header {
  max-height: calc(100dvh - 2.5rem);
}

.min-h-screen-without-header {
  min-height: calc(100dvh - 2.5rem);
}

.h-screen-without-header-footer {
  height: calc(100dvh - 2.5rem - 3rem - env(safe-area-inset-bottom));

  @media (min-width: 1024px) {
    height: calc(100dvh - 2.5rem);
  }
}

.max-h-screen-without-header-footer {
  max-height: calc(100dvh - 2.5rem - 3rem - env(safe-area-inset-bottom));

  @media (min-width: 1024px) {
    max-height: calc(100dvh - 2.5rem);
  }
}

.min-h-screen-without-header-footer {
  min-height: calc(100dvh - 2.5rem - 3rem - env(safe-area-inset-bottom));

  @media (min-width: 1024px) {
    min-height: calc(100dvh - 2.5rem);
  }
}

.content-shrink {
  width: 100%;

  animation-name: ContentSizeChanged;
  transition-duration: var(--transition-duration);
  animation-duration: var(--transition-duration);
  will-change: width;
  transition-timing-function: ease-out;

  @media (min-width: 1024px) {
    padding-right: 15rem;
  }
}

.content-expand {
  width: 100%;
  will-change: width;
  transition-duration: var(--transition-duration);
  animation-duration: var(--transition-duration);
  transition-timing-function: ease-out;
  animation-name: ContentSizeChanged;
}

@keyframes ContentSizeChanged {
  0% {
    transition-duration: var(--transition-duration);
  }

  99% {
    transition-duration: var(--transition-duration);
  }

  100% {
    transition-duration: 0s;
  }
}

@keyframes DarkInAnime {
  0% {
    background-color: #00000080;
    display: block;
  }

  100% {
    background-color: transparent;
    display: none;
  }
}

@keyframes DarkOutAnime {
  0% {
    background-color: transparent;
    display: block;
  }

  100% {
    background-color: #00000080;
    display: block;
  }
}

html,
body,
#app {
  height: 100%;
  background-color: var(--color-background);
}

body {
  font-weight: normal;
  line-height: 1.6;
  font-family: "Noto Sans JP", sans-serif;
  font-size: 16px;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: "pkna";
  color: var(--color-main-text);
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  touch-action: manipulation;
  overscroll-behavior-y: none;
}

.fonts-loaded body {
  font-family: "LINESeedJPStd", sans-serif;
}

.overflow-y-auto {
  overflow-y: auto;
  scrollbar-gutter: stable;
}
</style>

<style scoped lang="scss"></style>
